import { useDisableMfa } from '@myrentalmate/api-client';
import { Alert, Button, Chip, Paper, Stack, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { apiErrorMessage } from '../../lib/apiError';
import type { Me } from '../../lib/me';
import { TwoFactorSetup } from './TwoFactorSetup';

export function TwoFactorSection({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const [setupKey, setSetupKey] = useState(0);
  const [turningOff, setTurningOff] = useState(false);
  const [code, setCode] = useState('');
  const disable = useDisableMfa({
    mutation: {
      onSuccess: () => {
        setTurningOff(false);
        setCode('');
        setSetupKey((key) => key + 1);
        return queryClient.invalidateQueries();
      },
    },
  });

  function submitDisable(event: FormEvent) {
    event.preventDefault();
    disable.mutate({ data: { code } });
  }

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
        <Typography variant="h6">Two-Factor Sign-In</Typography>
        <Chip
          size="small"
          label={me.mfa_enabled ? 'On' : 'Off'}
          color={me.mfa_enabled ? 'success' : 'default'}
        />
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        A code from your phone, on top of your password, keeps your account safe if your password is
        ever stolen.
      </Typography>
      {me.mfa_enabled ? (
        turningOff ? (
          <Stack
            component="form"
            spacing={2}
            onSubmit={submitDisable}
            sx={{ alignItems: 'flex-start' }}
          >
            {disable.isError && <Alert severity="error">{apiErrorMessage(disable.error)}</Alert>}
            <TextField
              label="Code From Your App, or a Backup Code"
              required
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <Stack direction="row" spacing={1}>
              <Button type="submit" color="error" variant="outlined" disabled={disable.isPending}>
                Turn off two-factor
              </Button>
              <Button onClick={() => setTurningOff(false)}>Cancel</Button>
            </Stack>
          </Stack>
        ) : (
          <Button variant="outlined" onClick={() => setTurningOff(true)}>
            Turn off
          </Button>
        )
      ) : (
        /* Refreshed only once the backup codes are saved, so they stay on screen until then. */
        <TwoFactorSetup key={setupKey} onDone={() => void queryClient.invalidateQueries()} />
      )}
    </Paper>
  );
}
